import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../store/auth'
import { getVolumes, setMasterVolume, setMusicVolume, setSFXVolume, playSFX } from '../lib/audio'
import { subscribeToPush, unsubscribeFromPush, isPushSubscribed, isPushSupported } from '../lib/pushNotifications'
import { apiFetch } from '../api/client'

const NOTIF_PREFS_KEY = 'ab_notif_prefs'

const NOTIFICATION_TYPES = [
  { id: 'bounty_refresh',    label: 'Bounty Refresh',        desc: 'New spawns on the board',                priority: 'high'   },
  { id: 'rare_spawn',        label: 'Rare Spawn',            desc: 'Rift+ creature in your zone',            priority: 'high'   },
  { id: 'raid_starting',     label: 'Raid Starting',         desc: '30 min alert before raid events',        priority: 'high'   },
  { id: 'trade_response',    label: 'Trade Response',        desc: 'A player accepted or rejected your trade', priority: 'high' },
  { id: 'streak_reminder',   label: 'Streak Reminder',       desc: 'Login to keep your streak alive',        priority: 'medium' },
  { id: 'season_milestone',  label: 'Season XP Milestone',   desc: 'Reached a new Battle Pass tier',         priority: 'medium' },
  { id: 'awakening_ready',   label: 'Awakening Ready',       desc: 'Character meets stage conditions',       priority: 'medium' },
  { id: 'evolution_ready',   label: 'Evolution Ready',       desc: 'Creature meets stage 2 conditions',      priority: 'medium' },
  { id: 'season_ending',     label: 'Season Ending',         desc: '3 days left in current season',          priority: 'low'    },
  { id: 'new_character',     label: 'New Character',         desc: 'Seasonal character unlocked',            priority: 'low'    },
]

const PRIORITY_COLOR: Record<string, string> = {
  high: '#FF4800', medium: '#00F0FF', low: '#5A7A8A',
}

function loadNotifPrefs(): Record<string, boolean> {
  try {
    return JSON.parse(localStorage.getItem(NOTIF_PREFS_KEY) ?? '{}')
  } catch { return {} }
}

function saveNotifPrefs(prefs: Record<string, boolean>) {
  localStorage.setItem(NOTIF_PREFS_KEY, JSON.stringify(prefs))
}

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

export default function Settings() {
  const { signOut, player } = useAuth()
  const volumes = getVolumes()
  const [master, setMaster] = useState(volumes.master)
  const [music, setMusic] = useState(volumes.music)
  const [sfx, setSFX] = useState(volumes.sfx)
  const [notifPrefs, setNotifPrefs] = useState<Record<string, boolean>>(loadNotifPrefs)
  const [pushEnabled, setPushEnabled] = useState(false)
  const [pushPending, setPushPending] = useState(false)
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null)
  const [installState, setInstallState] = useState<'idle' | 'installed' | 'dismissed'>('idle')

  useEffect(() => {
    isPushSubscribed().then(setPushEnabled)
  }, [])

  const handlePushToggle = useCallback(async () => {
    if (pushPending) return
    setPushPending(true)
    try {
      if (pushEnabled) {
        await unsubscribeFromPush()
        setPushEnabled(false)
      } else {
        const ok = await subscribeToPush()
        setPushEnabled(ok)
      }
    } finally {
      setPushPending(false)
    }
  }, [pushEnabled, pushPending])

  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault()
      setInstallPrompt(e as BeforeInstallPromptEvent)
    }
    window.addEventListener('beforeinstallprompt', handler)
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setInstallState('installed')
    }
    return () => window.removeEventListener('beforeinstallprompt', handler)
  }, [])

  async function handleInstall() {
    if (!installPrompt) return
    await installPrompt.prompt()
    const { outcome } = await installPrompt.userChoice
    setInstallState(outcome === 'accepted' ? 'installed' : 'dismissed')
    setInstallPrompt(null)
  }

  function toggleNotif(id: string) {
    const next = { ...notifPrefs, [id]: !(notifPrefs[id] ?? true) }
    setNotifPrefs(next)
    saveNotifPrefs(next)
    // Sync to server fire-and-forget
    apiFetch('/notifications/preferences', { method: 'PATCH', body: JSON.stringify({ [id]: next[id] }) }).catch(() => {})
  }

  function isOn(id: string): boolean {
    return notifPrefs[id] ?? true
  }

  return (
    <div className="flex-1 flex flex-col" style={{ minHeight: '100vh', background: '#020510' }}>

      {/* Top Bar - Compact */}
      <div className="flex items-center justify-between p-4 border-b" style={{ borderColor: 'rgba(58,58,90,0.5)' }}>
        <h1 className="font-bold text-lg uppercase tracking-wider" style={{ color: '#EAF6FF', fontFamily: 'Rajdhani, system-ui' }}>Settings</h1>
      </div>

      {/* Content - Scrollable */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">

        {/* Install to home screen */}
        {installState !== 'installed' && (
          <div
            className="rounded-xl border p-4 flex items-center gap-4"
            style={{ borderColor: 'rgba(0,240,255,0.3)', background: 'rgba(0,240,255,0.05)' }}
          >
            <span className="text-3xl flex-shrink-0">📲</span>
            <div className="flex-1">
              <p className="font-bold text-sm text-[#EAF6FF] mb-0.5" style={{ fontFamily: 'Rajdhani, system-ui' }}>Install App</p>
              <p className="text-xs text-[#8A8AA5]" style={{ fontFamily: 'Inter, sans-serif' }}>Add to home screen for the full experience</p>
            </div>
            {installPrompt ? (
              <button onClick={handleInstall} 
                className="px-4 py-2 rounded-lg font-bold text-xs uppercase tracking-wider transition-all bg-[#00F0FF] text-[#0A0A12] hover:bg-[#39FF14]"
                style={{ fontFamily: 'Space Mono, monospace' }}>
                Install
              </button>
            ) : (
              <span className="text-[10px] text-[#5A5A7A] flex-shrink-0" style={{ fontFamily: 'Space Mono, monospace' }}>Open in browser →</span>
            )}
          </div>
        )}
        {installState === 'installed' && (
          <div className="rounded-xl border p-4 flex items-center gap-3" style={{ borderColor: 'rgba(57,255,20,0.3)', background: 'rgba(57,255,20,0.05)' }}>
            <span className="text-xl">✅</span>
            <p className="font-bold text-sm text-[#39FF14]" style={{ fontFamily: 'Rajdhani, system-ui' }}>App installed</p>
          </div>
        )}

        {/* Audio */}
        <div className="rounded-xl border p-5 space-y-4" style={{ background: 'rgba(13,13,21,0.95)', borderColor: 'rgba(58,58,90,0.5)' }}>
          <p className="text-[10px] text-[#5A5A7A] uppercase tracking-widest" style={{ fontFamily: 'Space Mono, monospace' }}>🔊 Audio</p>
          {[
            { label: 'Master', value: master, onChange: (v: number) => { setMaster(v); setMasterVolume(v) } },
            { label: 'Music',  value: music, onChange: (v: number) => { setMusic(v);  setMusicVolume(v) } },
            { label: 'SFX',    value: sfx,    onChange: (v: number) => { setSFX(v);   setSFXVolume(v) } },
          ].map(({ label, value, onChange }) => (
            <div key={label} className="flex items-center gap-4">
              <span className="font-bold text-sm text-[#EAF6FF] w-16 flex-shrink-0" style={{ fontFamily: 'Rajdhani, system-ui' }}>{label}</span>
              <input
                type="range" min={0} max={1} step={0.05}
                value={value}
                onChange={(e) => onChange(parseFloat(e.target.value))}
                className="flex-1 accent-[#FFE800] h-1"
              />
              <span className="font-bold text-xs text-[#FFE800] w-10 text-right" style={{ fontFamily: 'Space Mono, monospace' }}>
                {Math.round(value * 100)}%
              </span>
            </div>
          ))}
        </div>

        {/* Notifications */}
        <div className="rounded-xl border p-5" style={{ background: 'rgba(13,13,21,0.95)', borderColor: 'rgba(58,58,90,0.5)' }}>
          <div className="flex items-center justify-between mb-4">
            <p className="text-[10px] text-[#5A5A7A] uppercase tracking-widest" style={{ fontFamily: 'Space Mono, monospace' }}>🔔 Notifications</p>
            {isPushSupported() && (
              <button
                onClick={handlePushToggle}
                disabled={pushPending}
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors cursor-pointer border-none ${
                  pushEnabled ? 'bg-[#FFE800] bg-opacity-80' : 'bg-[#3A3A5A] bg-opacity-80'
                } ${pushPending ? 'opacity-50' : ''}`}
              >
                <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-[#EAF6FF] shadow transition-transform ${
                  pushEnabled ? 'translate-x-4.5' : 'translate-x-0.5'
                }`} />
              </button>
            )}
          </div>
          <div className="space-y-1">
            {NOTIFICATION_TYPES.map(n => (
              <button
                key={n.id}
                onClick={() => { playSFX('click'); toggleNotif(n.id) }}
                className="w-full flex items-center gap-3 py-2.5 px-1 rounded transition-colors hover:bg-white hover:bg-opacity-3 text-left cursor-pointer bg-transparent border-none"
              >
                <div
                  className="w-1.5 h-1.5 rounded-full flex-shrink-0 mt-0.5"
                  style={{ background: PRIORITY_COLOR[n.priority] }}
                />
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-sm text-[#EAF6FF]" style={{ fontFamily: 'Rajdhani, system-ui' }}>{n.label}</p>
                  <p className="text-xs text-[#5A5A7A] truncate" style={{ fontFamily: 'Inter, sans-serif' }}>{n.desc}</p>
                </div>
                {/* Toggle */}
                <div
                  className="relative w-10 h-5 rounded-full flex-shrink-0 transition-colors duration-200"
                  style={{
                    background: isOn(n.id) ? '#00F0FF30' : 'rgba(90,90,122,0.3)',
                    border: `1px solid ${isOn(n.id) ? '#00F0FF60' : 'rgba(90,90,122,0.5)'}`,
                  }}
                >
                  <div
                    className="absolute top-0.5 w-4 h-4 rounded-full transition-all duration-200"
                    style={{
                      background: isOn(n.id) ? '#00F0FF' : '#5A5A7A',
                      left: isOn(n.id) ? 'calc(100% - 18px)' : '2px',
                      boxShadow: isOn(n.id) ? '0 0 6px #00F0FF' : 'none',
                    }}
                  />
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* About / Credits */}
        <div className="rounded-xl border p-5 space-y-3" style={{ background: 'rgba(13,13,21,0.95)', borderColor: 'rgba(58,58,90,0.5)' }}>
          <p className="text-[10px] text-[#5A5A7A] uppercase tracking-widest" style={{ fontFamily: 'Space Mono, monospace' }}>About</p>

          {[
            { label: 'Version',  value: '0.1.0 Beta' },
            { label: 'Studio',   value: 'Ploonu LLC' },
            { label: 'Player ID', value: player?.id?.slice(0, 12) ?? '—' },
            { label: 'Status',   value: player?.isFoundingMember ? '🌟 Founding Member' : 'Beta Tester' },
          ].map(row => (
            <div key={row.label} className="flex justify-between items-center">
              <span className="text-sm text-[#8A8AA5]" style={{ fontFamily: 'Inter, sans-serif' }}>{row.label}</span>
              <span className="font-bold text-xs text-[#EAF6FF]" style={{ fontFamily: 'Rajdhani, system-ui' }}>{row.value}</span>
            </div>
          ))}

          <div className="pt-2 border-t border-[#3A3A5A]">
            <p className="text-[10px] text-[#5A5A7A] mb-1.5 uppercase tracking-wider" style={{ fontFamily: 'Space Mono, monospace' }}>Credits</p>
            {[
              { name: 'Kenney',          desc: 'SFX & UI assets',         url: 'kenney.nl' },
              { name: 'Kevin MacLeod',   desc: 'Music (incompetech.com)', url: 'incompetech.com' },
              { name: 'game-icons.net',  desc: 'Element icons',           url: 'game-icons.net' },
            ].map(c => (
              <div key={c.name} className="flex items-center justify-between py-1">
                <span className="font-bold text-xs text-[#EAF6FF]" style={{ fontFamily: 'Rajdhani, system-ui' }}>{c.name}</span>
                <span className="text-xs text-[#5A5A7A]" style={{ fontFamily: 'Inter, sans-serif' }}>{c.desc}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Sign out */}
        <button
          onClick={() => { signOut(); window.location.href = '/landing'; }}
          className="w-full py-3 text-sm font-bold uppercase tracking-wider rounded-lg border transition-all cursor-pointer bg-transparent"
          style={{ borderColor: 'rgba(255,72,0,0.3)', color: '#FF4800', fontFamily: 'Space Mono, monospace' }}
          onMouseEnter={e => { (e.target as HTMLButtonElement).style.borderColor = 'rgba(255,72,0,0.6)'; (e.target as HTMLButtonElement).style.background = 'rgba(255,72,0,0.1)'; }}
          onMouseLeave={e => { (e.target as HTMLButtonElement).style.borderColor = 'rgba(255,72,0,0.3)'; (e.target as HTMLButtonElement).style.background = 'transparent'; }}
        >
          Sign Out
        </button>

      </div>
    </div>
  )
}
